/**
 * `NetClient` - the frozen transport-facing API of the client.
 *
 * Message flow implemented here (matching `packages/shared` exactly, nothing else):
 *
 *   connect() -> transport join -> `hello{v, name}`
 *     <- `welcome{playerId, tick, worldSeed, spawn, config, capabilities?}`  (streaming + prediction start here)
 *     -> `sub{keys[]}`  (<=128 keys per frame, only for chunks we do not already hold)
 *     <- `chunk{snapshot}`  (authoritative voxel state, resyncs the local chunk)
 *     -> `unsub{keys[]}`  (<=128 keys per frame, only for chunks the view just evicted, and only when
 *                          `welcome.capabilities` advertises `unsub`: an older server has no `unsub`
 *                          handler for it, and Colyseus closes the connection instead of replying)
 *     -> `input{seq, dt, move, look, actions}`  (10 Hz, latest sample reused between ticks)
 *     <- `snapshot{tick, players}`  (remote players only; NEVER the local player)
 *     <- `ack{seq, tick, pos, vel}`  (the only thing that may move the local player)
 *     -> `setBlock{key, x, y, z, block, rev}`  (optimistic, applied locally first)
 *     <- `blockAck{key, x, y, z, block, rev, ok}`  (ok:false rolls the prediction back)
 *     -> `ping{ts}` / <- `pong{ts}`  (RTT for the HUD)
 *
 * The Colyseus transport is the ONLY transport (`transport.ts`): the authority upgrades and seats
 * connections through the Colyseus envelope, so a plain `WebSocket` could connect and then never be
 * sent a `welcome`. A join that fails or times out is therefore TERMINAL: status `error`, no second
 * attempt, no "connected" (an outage must not look like a partial success). `kind` reports the live
 * transport, or `"none"` while there is not one.
 *
 * Nothing in this file knows about three.js, the world or the loop, which is what keeps it testable in
 * a plain DOM-less harness.
 */

import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from "@rogueworlds/shared";
import { ColyseusTransport, type Transport } from "./transport";

export interface NetConfig {
  readonly endpoint: string;
  readonly name: string;
  readonly onStatus: (s: NetStatus) => void;
}

export type NetStatus = "connecting" | "connected" | "disconnected" | "error";

type Handler<T extends ServerMessage["t"]> = (msg: Extract<ServerMessage, { t: T }>) => void;
type AnyHandler = (msg: ServerMessage) => void;

/** How long a single transport attempt may take before the attempt is failed for good. */
const CONNECT_TIMEOUT_MS = 8000;
/** Protocol `ping` cadence. */
const PING_INTERVAL_MS = 2000;

export class NetClient {
  private readonly config: NetConfig;
  private readonly handlers = new Map<ServerMessage["t"], Set<AnyHandler>>();
  private transport: Transport | undefined = undefined;
  private statusValue: NetStatus = "disconnected";
  private pingMs = 0;
  private connectAttempt = 0;
  private pingTimer: number | undefined = undefined;
  private droppedFrames = 0;

  constructor(config: NetConfig) {
    this.config = config;
  }

  get status(): NetStatus {
    return this.statusValue;
  }

  /** Last measured round-trip time in milliseconds; 0 until a `pong` arrives. */
  get ping(): number {
    return this.pingMs;
  }

  /**
   * Which transport actually carries the protocol: `"colyseus"` only once a seat has been joined, and
   * `"none"` while there is no transport at all. There is no second transport to report, so this can
   * never claim a connection the client does not have.
   */
  get kind(): "colyseus" | "none" {
    return this.transport?.kind ?? "none";
  }

  /** Frames dropped because the transport was not connected. */
  get dropped(): number {
    return this.droppedFrames;
  }

  /** Starts (or restarts) the connection. Safe to call again from the error overlay's retry. */
  connect(): void {
    if (this.statusValue === "connecting" || this.statusValue === "connected") {
      return;
    }
    const attempt = this.connectAttempt + 1;
    this.connectAttempt = attempt;
    void this.runConnect(attempt);
  }

  send(msg: ClientMessage): void {
    const transport = this.transport;
    if (transport !== undefined && this.statusValue === "connected") {
      transport.send(msg);
      return;
    }
    this.droppedFrames += 1;
  }

  /** Registers a handler for one server frame type. Returns an unsubscriber. */
  on<T extends ServerMessage["t"]>(type: T, handler: Handler<T>): () => void {
    let set = this.handlers.get(type);
    if (set === undefined) {
      set = new Set<AnyHandler>();
      this.handlers.set(type, set);
    }
    const entry: AnyHandler = (message: ServerMessage) => {
      handler(message as Extract<ServerMessage, { t: T }>);
    };
    set.add(entry);
    return () => {
      const current = this.handlers.get(type);
      if (current !== undefined) {
        current.delete(entry);
      }
    };
  }

  close(): void {
    this.connectAttempt += 1;
    this.stopPingTimer();
    const transport = this.transport;
    this.transport = undefined;
    if (transport !== undefined) {
      transport.dispose();
    }
    this.setStatus("disconnected");
  }

  // ---------------------------------------------------------------- internals

  private async runConnect(attempt: number): Promise<void> {
    this.setStatus("connecting");
    try {
      await this.openTransport(attempt);
    } catch (error) {
      if (this.connectAttempt !== attempt) {
        return;
      }
      // TERMINAL, on purpose: the authority seats connections through the Colyseus envelope only, so
      // there is no second transport to try and no "connected" to report. Saying `connected` here (or
      // routing the frames at a plain WebSocket that can never be sent a `welcome`) would turn a real
      // outage into an apparent partial success in the HUD and in the logs.
      console.error(
        `[net] Colyseus join failed (${describe(error)}); the authority could not be reached ` +
          `over the Colyseus transport at ${this.config.endpoint}.`,
      );
      this.setStatus("error");
    }
  }

  private async openTransport(attempt: number): Promise<void> {
    const events = {
      onMessage: (message: ServerMessage): void => {
        this.dispatch(message);
      },
      onClose: (reason: string): void => {
        if (this.connectAttempt !== attempt || this.transport === undefined) {
          return;
        }
        console.warn(`[net] transport closed: ${reason}`);
        this.stopPingTimer();
        this.transport = undefined;
        this.setStatus("disconnected");
      },
    };
    const transport: Transport = new ColyseusTransport(this.config.endpoint, this.config.name, events);
    try {
      await withTimeout(transport.connect(), CONNECT_TIMEOUT_MS, "colyseus connect timed out");
    } catch (error) {
      // A join that fails or times out must not leave a socket behind: `dispose()` is what makes the
      // seat that may still arrive be handed straight back instead of delivering a late `welcome`
      // into a client whose status is already `error`.
      transport.dispose();
      throw error;
    }
    if (this.connectAttempt !== attempt) {
      transport.dispose();
      return;
    }
    this.transport = transport;
    this.setStatus("connected");
    // Handshake: the world protocol's own hello, after the framework-level join succeeded.
    transport.send({ t: "hello", v: PROTOCOL_VERSION, name: this.config.name });
    this.startPingTimer();
  }

  private dispatch(message: ServerMessage): void {
    if (message.t === "pong") {
      this.pingMs = performanceNow() - message.ts;
    }
    const set = this.handlers.get(message.t);
    if (set === undefined) {
      return;
    }
    for (const handler of set) {
      try {
        handler(message);
      } catch (error) {
        console.error(`[net] ${message.t} handler failed: ${describe(error)}`);
      }
    }
  }

  private startPingTimer(): void {
    this.stopPingTimer();
    this.pingTimer = window.setInterval(() => {
      this.send({ t: "ping", ts: performanceNow() });
    }, PING_INTERVAL_MS);
  }

  private stopPingTimer(): void {
    if (this.pingTimer !== undefined) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = undefined;
    }
  }

  private setStatus(status: NetStatus): void {
    if (this.statusValue === status) {
      return;
    }
    this.statusValue = status;
    this.config.onStatus(status);
  }
}

function performanceNow(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}
